/**
 * Figma Node IDs:
 * Design Frame: 1493:72731
 * Instance IDs: I1493:71779, I1493:71850, I1493:71928, I1493:71949, I1493:71970, I1493:71991
 * Composite: true
 * Last Updated: 2024-12-22
 * Generated by: Claude Code figma-component command
 */
import React, { useId, useState, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";

import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { SingleSelectQuestionProps } from "./types";
import { TableQuestion } from "../TableQuestion";
import { fetchTranslations } from "./locales/config";
import { RadioButtonGroup } from "./RadioButtonGroup";
import { Figure } from "../Figure";
import { Collapsible } from "../LxCollapsible";
import { LxButton } from "../LxButton";
import { FeedbackBanner } from "../FeedbackBanner";
import { useSingleSelect } from "./useSingleSelect";
import {
  singleSelectQuestionClass,
  containerClass,
  questionCategoryClass,
  questionTextClass,
  questionTextParagraphClass,
  instructionTextClass,
  optionsSectionClass,
  buttonSlabClass,
  attemptsTextClass,
  predictionFeedbackWrapperClass,
  collapsibleClass,
} from "./SingleSelectQuestion.css";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  attemptsLeft: {
    id: "Question.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description:
      "Label showing the number of attempts left for the question. {attemptsLeft} is a placeholder for the actual number.",
  },
  submitAnswer: {
    id: "Question.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the button to submit the selected answer.",
  },
  submitPrediction: {
    id: "Question.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the button to submit the selected prediction.",
  },
  resetQuestion: {
    id: "Question.reset",
    defaultMessage: "Reset question",
    description: "Text for the button to reset the question and try again.",
  },
  resetSelection: {
    id: "Question.predictionReset",
    defaultMessage: "Reset prediction",
    description: "Text for the button to reset the question and try again.",
  },
  resetQuestionAriaLabel: {
    id: "Question.resetQuestionAriaLabel",
    defaultMessage: "Reset question button",
    description:
      "Aria label for the button to reset the question and try again.",
  },
  resetPredictionAriaLabel: {
    id: "Question.resetPredictionAriaLabel",
    defaultMessage: "Reset prediction button",
    description:
      "Aria label for the button to reset the prediction and try again.",
  },
  predictionSubmitted: {
    id: "Question.predictionSubmitted",
    defaultMessage:
      "<b>Prediction submitted.</b> You will compare this answer with your final result at the end of the experiment.",
    description:
      "Text shown in the banner when a prediction question has been submitted.",
  },
  predictionHeader: {
    id: "Question.predictionHeader",
    defaultMessage: "Prediction Question",
    description: "Header text for prediction question type.",
  },
  instructionText: {
    id: "Question.instructionText",
    defaultMessage: "(Select the best option.)",
    description:
      "Instruction text telling users to select the best option for the question.",
  },
});

const QuestionHeader: React.FC<{
  id: string;
  isPrediction: boolean;
}> = ({ id, isPrediction }) => {
  return isPrediction ? (
    <p id={id} className={questionCategoryClass}>
      <RichText value={messages.predictionHeader} />
    </p>
  ) : null;
};
/* ---------------------------- UI Subcomponents ---------------------------- */

const QuestionFooter: React.FC<{
  attemptsLeft: number;
  showReset: boolean;
  onSubmit: () => void;
  onReset: () => void;
  isSubmitDisabled: boolean;
  isPredictionQuestion: boolean;
}> = ({
  attemptsLeft,
  showReset,
  onSubmit,
  onReset,
  isSubmitDisabled,
  isPredictionQuestion,
}) => {
  const { formatMessage } = useIntl();
  const submitText = formatMessage(
    isPredictionQuestion ? messages.submitPrediction : messages.submitAnswer
  );
  const resetText = formatMessage(
    isPredictionQuestion ? messages.resetSelection : messages.resetQuestion
  );
  const resetAriaLabel = formatMessage(
    isPredictionQuestion
      ? messages.resetPredictionAriaLabel
      : messages.resetQuestionAriaLabel
  );
  const submitButtonType = isPredictionQuestion ? "alternative" : "primary";

  return (
    <div className={buttonSlabClass} data-name="Button slab">
      {attemptsLeft > -1 && (
        <p
          className={attemptsTextClass}
          data-testid="attempts-left"
          aria-live="polite"
          role="status"
        >
          <RichText
            value={{
              ...messages.attemptsLeft,
              placeholderValues: { attemptsLeft },
            }}
          />
        </p>
      )}
      {showReset ? (
        <LxButton
          variant="secondary"
          text={resetText}
          ariaLabel={resetAriaLabel}
          size="large"
          onClickHandler={onReset}
        />
      ) : (
        <LxButton
          variant={submitButtonType}
          text={submitText}
          size="large"
          onClickHandler={onSubmit}
          disabled={isSubmitDisabled}
        />
      )}
    </div>
  );
};

/* ---------------------------- Main Component ---------------------------- */

const SingleSelectQuestionInternal: React.FC<SingleSelectQuestionProps> = ({
  id,
  content,
  question,
  text,
  image,
  dataTable,
  options,
  answerId,
  hintDetails,
  isPredictionQuestion = false,
  onOptionSelect,
  onSubmit,
  onReset,
  setCompleted,
  totalAttempts = -1, // -1 means unlimited
}) => {
  const [questionState, questionActions] = useSingleSelect(id);

  const { selectedOptionIndex, submittedOptionIndexes } = questionState;

  // Component-specific state
  const [attemptsLeft, setAttemptsLeft] = useState<number>(totalAttempts);
  const [resetCount, setResetCount] = useState(0);

  const elementId = useId();
  const headerId = `${elementId}-header`;
  const textId = `${elementId}-text`;
  const questionId = `${elementId}-question`;
  const imageId = `${elementId}-image`;
  const imageCaptionId = `${elementId}-caption`;

  /* ---------------------------- Derived Booleans ---------------------------- */

  const hasSubmittedAnswers = submittedOptionIndexes.length > 0;

  const hasCorrectAnswer = submittedOptionIndexes.some((idx) =>
    answerId.includes(idx)
  );

  const hasWrongAnswer = submittedOptionIndexes.some(
    (idx) => !answerId.includes(idx)
  );

  const showResetButton = isPredictionQuestion
    ? hasSubmittedAnswers
    : hasSubmittedAnswers && hasCorrectAnswer;

  const showPredictionFeedback = isPredictionQuestion && hasSubmittedAnswers;

  const isSubmitDisabled =
    selectedOptionIndex === undefined ||
    (!isPredictionQuestion &&
      hasWrongAnswer &&
      submittedOptionIndexes.includes(selectedOptionIndex) &&
      !hasCorrectAnswer);

  const isOptionSelectionDisabled =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    attemptsLeft === 0;

  const isCompleted =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    (attemptsLeft === 0 && hasSubmittedAnswers);

  // Notify completion
  useEffect(() => {
    if (isCompleted) {
      setCompleted?.(true);
    }
  }, [isCompleted, setCompleted]);

  /* ---------------------------- Handlers ---------------------------- */

  const handleSelectOption = (optionIndex: number) => {
    questionActions.selectOption(optionIndex);
    onOptionSelect?.(optionIndex);
  };

  const handleSubmit = () => {
    if (selectedOptionIndex === undefined) return;

    const isCorrectAnswer = answerId.includes(selectedOptionIndex);
    const willRunOutOfAttempts = attemptsLeft === 1;
    const willBeCompleted =
      isCorrectAnswer || willRunOutOfAttempts || isPredictionQuestion;

    setAttemptsLeft((prev) => {
      if (prev > 0) return prev - 1;
      if (prev === -1) return -1;
      return 0;
    });

    questionActions.submit();

    if (willBeCompleted) {
      setCompleted?.(true);
    }

    onSubmit?.();
  };

  const handleReset = () => {
    questionActions.reset();
    setAttemptsLeft(totalAttempts);
    setResetCount((prev) => prev + 1);
    onReset?.();
  };

  /* ---------------------------- Dynamic Content ---------------------------- */

  const buildAriaLabelledby = () => {
    const ids: string[] = [headerId];
    if (text) ids.push(textId);
    ids.push(questionId);
    if (image) ids.push(imageId);
    if (image?.caption) ids.push(imageCaptionId);
    return ids.join(" ");
  };

  const ariaLabelledby = buildAriaLabelledby();

  const renderContent = () => {
    const elements: React.ReactNode[] = [];
    elements.push(
      <QuestionHeader
        key="header"
        id={headerId}
        isPrediction={isPredictionQuestion}
      />
    );
    content.forEach((type, index) => {
      switch (type) {
        case "text":
          if (text) {
            elements.push(
              <div
                id={textId}
                key={`text-${index}`}
                className={questionTextClass}
              >
                <div className={questionTextParagraphClass}>
                  <RichText value={text} addTagClasses={false} />
                </div>
              </div>
            );
          }
          break;
        case "question":
          elements.push(
            <div
              id={questionId}
              key={`q-${index}`}
              className={questionTextClass}
            >
              <div className={questionTextParagraphClass}>
                <RichText value={question} addTagClasses={false} />
              </div>
              <p className={instructionTextClass}>
                <RichText value={messages.instructionText} />
              </p>
            </div>
          );
          break;
        case "image":
          if (image) {
            elements.push(
              <Figure
                key={`img-${index}`}
                src={image.src}
                altText={image.altText}
                caption={image.caption}
                imageId={imageId}
                captionId={image.caption ? imageCaptionId : undefined}
                {...(image.containerBgColor
                  ? { containerBgColor: image.containerBgColor }
                  : {})}
              />
            );
          }
          break;
        case "dataTable":
          if (dataTable) {
            elements.push(
              <TableQuestion
                key={`table-${index}`}
                id={index}
                type="DATA_TABLE"
                data={dataTable.data}
                content={["table"]}
                isTransparent={true}
              />
            );
          }
          break;
        case "options":
          elements.push(
            <div
              key={`opt-${index}`}
              className={optionsSectionClass}
              data-name="Options"
            >
              <RadioButtonGroup
                options={options}
                selectedOptionIndex={selectedOptionIndex}
                submittedOptionIndexes={submittedOptionIndexes}
                answerId={answerId}
                onOptionSelect={
                  isOptionSelectionDisabled ? undefined : handleSelectOption
                }
                ariaLabelledby={ariaLabelledby}
                showFeedbackOnSubmit={!isPredictionQuestion}
                resetCount={resetCount}
              />
            </div>
          );
          break;
        case "hintDetails":
          if (hintDetails) {
            elements.push(
              <div className={collapsibleClass} key={`hint-${index}`}>
                <Collapsible
                  label={hintDetails.label}
                  content={hintDetails.content}
                  open={hintDetails.open}
                />
              </div>
            );
          }
          break;
        default:
          break;
      }
    });

    return elements;
  };

  /* ---------------------------- Render ---------------------------- */

  return (
    <div
      className={singleSelectQuestionClass}
      data-name="Single-select question"
    >
      <div className={`${containerClass}`}>
        {renderContent()}
        <div
          aria-live="polite"
          role="status"
          key={`prediction-feedback-${resetCount}`}
        >
          {showPredictionFeedback && (
            <FeedbackBanner
              variant="information"
              message={messages.predictionSubmitted}
              className={predictionFeedbackWrapperClass}
            />
          )}
        </div>
        <QuestionFooter
          attemptsLeft={attemptsLeft}
          showReset={showResetButton}
          onSubmit={handleSubmit}
          onReset={handleReset}
          isSubmitDisabled={isSubmitDisabled}
          isPredictionQuestion={isPredictionQuestion}
        />
      </div>
    </div>
  );
};

export const SingleSelectQuestion = (props: SingleSelectQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <SingleSelectQuestionInternal {...props} />
  </TranslationsProvider>
);
